import { Bounce, toast } from "react-toastify";
import { getAicServerUrl } from "../config";
import {
  CALL_CANNOT_DIAL,
  permissionName,
  SHOW_NOTIFICATION,
} from "../constants/constants";
import storeRTK from "../store/storeRTK";
import { ConnectInstance } from "../types/agent.type";
import { RootState } from "../store/storeConfig";
import { MakeCallOptions } from "../types/generic.types";
import { API_CALL_DIAL } from "../constants/events";
import Emitter from "../emitter";
import { session } from "../controller/session";
import { isDialCallInProgress } from "../actions/call";

/**
 * Returns a shallow clone of the object provided
 * @param {T} obj
 * @returns{T} Cloned Object
 */
export function shallowClone<T>(obj: T): T {
  return Object.assign({}, obj);
}

/**
 * Used to generate UUID's for Session Handling
 * @returns Randomly Generated UUID Session
 */
export function makeUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (a, b) {
      return (
        (b = Math.random() * 16), (a == "y" ? (b & 3) | 8 : b | 0).toString(16)
      );
    }
  );
}

export async function checkVpnConn() {
  const CURRENT_DOMAIN = await getAicServerUrl();
  return new Promise((resolve, reject) => {
    fetch(`${CURRENT_DOMAIN}/v1/ready`)
      .then((response) => response.text())
      .then(() => {
        resolve("ok");
      })
      .catch(() => {
        reject("failure");
      });
  });
}
export function checkVpnConnNTimes(
  maxRetry: number,
  callback: (error: any, status: boolean) => void,
  err?: any
) {
  if (maxRetry <= 0) {
    maxRetry = 0;
    callback(err, false);
  } else {
    if (navigator.onLine) {
      checkVpnConn()
        .then(() => callback(null, true))
        .catch((err) => {
          setTimeout(
            () => checkVpnConnNTimes(maxRetry - 1, callback, err),
            1000
          );
        });
    } else {
      setTimeout(() => checkVpnConnNTimes(maxRetry - 1, callback, err), 3000);
    }
  }
}

/**
 * A utility function to perform HTTP requests with enhanced features such as:
 * - Automatic query parameter handling
 * - Basic authentication support
 * - JSON request/response handling
 * - Custom success status validation
 * - Blob response support
 *
 * @param {Object} params - The configuration object for the request.
 * @param {string} params.url - The URL to send the request to.
 * @param {string} params.method - The HTTP method (e.g., "GET", "POST", "PUT", etc.).
 * @param {Object} [params.queryParams] - An object representing query parameters to append to the URL.
 * @param {string} [params.username] - The username for basic authentication.
 * @param {string} [params.password] - The password for basic authentication.
 * @param {number} [params.successStatus] - A specific HTTP status code to treat as a successful response.
 * @param {Object} [params.json] - The JSON payload to send in the request body (for POST, PUT, PATCH).
 * @param {boolean} [params.isJsonResponse] - Whether to force treating the response as JSON.
 * @param {string} [params.responseType] - The expected response type (e.g., "blob").
 *
 * @returns {Promise<any>} - A promise that resolves to the response data or rejects with an error.
 *
 * This function:
 * 1. Constructs headers based on the provided parameters (e.g., JSON content type, basic auth).
 * 2. Appends query parameters to the URL if provided.
 * 3. Sends the HTTP request using the Fetch API with appropriate options.
 * 4. Handles errors by parsing the response body (JSON or text) and throwing a descriptive error.
 * 5. Supports different response types (e.g., JSON, blob).
 */
export function customFetch({
  url,
  method,
  queryParams,
  username,
  password,
  successStatus,
  json,
  isJsonResponse,
  responseType,
}: any): Promise<any> {
  const headers: any = {};

  if (method === "GET") headers["Accept"] = "*/*";

  if (username && password) {
    headers["Authorization"] = `Basic ${btoa(`${username}:${password}`)}`;
  }

  if (json) {
    headers["Content-Type"] = "application/json";
  }

  if (queryParams) {
    const uSearchParams = new URLSearchParams();
    for (const key in queryParams) {
      if (queryParams[key]) uSearchParams.set(key, queryParams[key]);
    }
    url += `?${uSearchParams.toString()}`;
  }

  return fetch(url, {
    method,
    mode: "cors",
    credentials: "include",
    redirect: "follow",
    referrerPolicy: "no-referrer",
    keepalive: true,
    headers,
    body:
      method === "POST" || method === "PUT" || method === "PATCH"
        ? JSON.stringify(json, undefined, 2)
        : undefined,
  }).then(async (response) => {
    if ((successStatus && response.status !== successStatus) || !response.ok) {
      let errorBody: any = {};
      try {
        errorBody = await response.clone().json();
      } catch {
        try {
          errorBody = await response.text();
        } catch {
          errorBody = {};
        }
      }

      const errorMessage =
        errorBody?.error ||
        errorBody?.message ||
        (typeof errorBody === "string"
          ? errorBody
          : `HTTP error ${response.status}`);

      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).body = errorBody;

      throw error;
    }

    if (responseType === "blob") {
      return response;
    }

    const isJson =
      response.headers.get("content-type")?.includes("application/json") ||
      isJsonResponse;

    return isJson ? await response.json() : response;
  });
}

/**
 * A utility function to execute an API call with retry logic.
 *
 * @param {Function} apiFunc - The API function to be executed. It should return a Promise.
 * @param {any} apiParams - The parameters to be passed to the API function.
 * @param {number} [maxRetries=2] - The maximum number of retry attempts. Defaults to 2 if not provided.
 *
 * @returns {Promise<any>} - A Promise that resolves with the API response or rejects after all retries fail.
 *
 * This function:
 * 1. Defines retry options, including the maximum number of retries and timeout intervals between retries.
 * 2. Attempts to execute the API function.
 * 3. If the API call fails, retries the call after a specified timeout, up to the maximum number of retries.
 * 4. Resolves the Promise if the API call succeeds, or rejects it if all retries fail.
 */
export function fetchWithRetry(
  apiFunc: any,
  apiParams: any,
  maxRetries?: number
): Promise<any> {
  const retryOpts = {
    maxRetries: maxRetries || 2,
    timeouts: [1000, 1500, 3000],
  };

  return new Promise(function (resolve, reject) {
    let currentAttempt = 0;
    function runApi() {
      currentAttempt++;
      apiFunc(apiParams)
        .then((response: any) => {
          resolve(response);
        })
        .catch((err: any) => {
          if (currentAttempt < retryOpts.maxRetries) {
            setTimeout(runApi, retryOpts.timeouts[currentAttempt - 1]);
          } else {
            reject(err);
          }
        });
    }

    runApi();
  });
}

export function formatPhoneNumber(phoneNumber: string) {
  // Trim any leading and trailing whitespace
  phoneNumber = phoneNumber.trim();

  // Check if phoneNumber starts with '+'
  if (phoneNumber.startsWith("+")) {
    return phoneNumber;
  }

  // Add '+' prefix if it's not present
  return "+" + phoneNumber;
}

// Function to merge two arrays with unique elements based on a key
type AnyObject = { [key: string]: any };

// Function to merge two arrays with unique elements based on a specific key
export function mergeUnique<T extends AnyObject>(
  arr1: T[],
  arr2: T[],
  key: keyof T
): T[] {
  const unique = new Map(arr1.map((item) => [item[key], item]));

  arr2.forEach((item) => {
    if (!unique.has(item[key])) {
      unique.set(item[key], item);
    }
  });

  return Array.from(unique.values());
}

export const checkInternet = async () => {
  const CURRENT_DOMAIN = await getAicServerUrl();
  return fetch(`${CURRENT_DOMAIN}/v1/ready`)
    .then((response) => {
      if (response.ok) {
        return true;
      } else {
        // throw new Error("Server response was not ok.");
        console.log("Server response was not ok.");
      }
    })
    .catch(() => false);
};

// Rename the keys
export function renameKeys(
  obj: any,
  replaceThis: string,
  replacewith: string
): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => renameKeys(item, replaceThis, replacewith));
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const newKey = key === replaceThis ? replacewith : key;
      acc[newKey] = renameKeys(obj[key], replaceThis, replacewith);
      return acc;
    }, {} as any); // Add type annotation here
  } else {
    return obj;
  }
}

export const formatTimeForCall = (callTime: number) => {
  const hours = Math.floor(callTime / 3600000)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((callTime % 3600000) / 60000)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor((callTime % 60000) / 1000)
    .toString()
    .padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

/**
 * Displays an error toast notification to the user.
 * This function creates and shows a toast message with the provided error details.
 * It is typically used to inform the user of an error that has occurred in the application.
 *
 * @param {string} msg - The error message to be displayed in the toast.
 * @param {number} id - The unique id for the toast.
 * @returns {void}
 */
export const showErrorToast = (msg: string, id: string) => {
  return toast.error(msg, {
    toastId: id,
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    theme: "light",
    transition: Bounce,
  });
};

//logout all tabs
export const logoutChannel = new BroadcastChannel("logout");

/**
 * Checks if the necessary media permissions (e.g., microphone, camera) are granted.
 * This function requests the media permissions from the user and verifies if they are granted.
 *
 * @returns {Promise<boolean>}
 *          A promise that resolves to true if the permissions are granted, otherwise false.
 */
export function checkMediaPermission(callback: any, flag?: boolean) {
  navigator.permissions
    .query({ name: permissionName })
    .then(function (result) {
      if (result.state === "granted") {
        callback(true);
      } else {
        callback(false);
      }
    })
    .catch(() => {
      if (!flag) {
        setTimeout(() => checkMediaPermission(callback, true), 500);
      } else {
        callback(false);
      }
    });
}

/**
 * Retrieves the alias for the current instance.
 * This function is used to identify the active instance of the application.
 *
 * @returns {string} The alias of the current instance.
 */
export const getInstanceAlias = (): string => {
  const state = storeRTK.getState();
  const activeInstance = state.agent?.instanceConfig?.find(
    (instance: ConnectInstance) => instance.active === "true"
  );
  return activeInstance?.instance_alias;
};

export const getAmazonConnectUrlByContactId = (contactId: string): string => {
  const state = storeRTK.getState();
  const agentUrl = state?.agent?.aicConfig?.ccp_url.replace("/ccp-v2", "");
  const amazonConnectUrl = `${agentUrl}/contact-trace-records/details/${contactId}`;
  return amazonConnectUrl;
};

export const getSafeUrl = (url: string): string | null => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    return ["http:", "https:"].includes(parsedUrl.protocol)
      ? parsedUrl.href
      : null;
  } catch {
    return null;
  }
};

export const getInstanceAliasFromStore = () => {
  const instanceAlias = storeRTK.getState().agent.instanceConfig?.[0]
    ?.instance_alias
    ? `/${storeRTK.getState().agent.instanceConfig[0].instance_alias}`
    : "";
  return instanceAlias;
};

// Format helpers
export const formatTime = (date: Date) => {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

const formatDate = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleString("en-US", options);
};

export const formatTimeString = (connectedTime: any) => {
  if (!connectedTime) {
    connectedTime = new Date();
  }

  const connectedDate = new Date(connectedTime);
  const now = new Date();

  // Check for invalid date
  if (isNaN(connectedDate.getTime())) {
    return "Invalid Time";
  }

  // Calculate time differences
  const diffInSeconds = Math.floor(
    (now.getTime() - connectedDate.getTime()) / 1000
  );
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  // const diffInDays = Math.floor(diffInHours / 24);

  // Get relative time display
  if (diffInSeconds < 60) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    // } else if (diffInDays === 1) {
    //   return `Yesterday ${formatTime(connectedDate)}`;
    // } else if (diffInDays < 7) {
    //   return `${diffInDays} days ago`;
  } else {
    return `${formatDate(connectedDate)}`;
  }
};

export const formatDuration = (startTime: any, endTime: any) => {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);

  // Check for invalid dates
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return "0s";
  }

  const durationInSeconds = Math.floor(
    (endDate.getTime() - startDate.getTime()) / 1000
  );
  const durationHours = Math.floor(durationInSeconds / 3600);
  const durationMinutes = Math.floor((durationInSeconds % 3600) / 60);
  const durationSeconds = durationInSeconds % 60;

  const durationParts = [];
  if (durationHours > 0) durationParts.push(`${durationHours}h`);
  if (durationMinutes > 0) durationParts.push(`${durationMinutes}min`);
  if (durationSeconds > 0) durationParts.push(`${durationSeconds}s`);

  return durationParts.join(" ") || "0s";
};
/**
 * Checks if a given tab is accessible based on the allowed features in the store.
 *
 * @param tabName - The name of the tab to check access for.
 * @returns A boolean indicating whether the tab is accessible.
 *
 * The function retrieves the `allowed_features` array from the Redux store.
 * It then checks if any object in the array contains the given `tabName` as a key
 * with a value of either the string `"true"` or the boolean `true`.
 */
export const tabAccessControl = (tabName: string): string => {
  const allowedFeatures = storeRTK.getState().agent?.allowed_features;
  const isAllowed = allowedFeatures?.some(
    (feature: Record<string, string | boolean>) =>
      feature[tabName] === "true" || feature[tabName] === true
  );
  return isAllowed ? "true" : "false";
};

/**
 * Formats a given label string by replacing underscores and hyphens with spaces,
 * capitalizing the first letter of each word, and converting the rest of the word to lowercase.
 *
 * @param label - The input string to format.
 * @returns A formatted string with proper capitalization and spaces, or an empty string if the input is invalid.
 *
 * Example:
 * Input: "example_label-name"
 * Output: "Example Label Name"
 */
export function formatLabel(label: string): string {
  return (
    label
      ?.replace(/[_-]/g, " ")
      .split(" ")
      .map((word) => word[0]?.toUpperCase() + word.slice(1).toLowerCase())
      .join(" ") || ""
  );
}

/**
 * Persists the history of multiple instances for a specific agent into local storage.
 * This function ensures that the history of each instance is saved and associated
 * with the correct agent, allowing for multi-instance state management.
 *
 * @param state - The current Redux state containing agent and history information.
 */
export const persistMultiInstanceHistory = (state: RootState) => {
  const agent_id = state.agent.id as string;
  const activeInstance = getInstanceAlias();

  const existingData = JSON.parse(localStorage.getItem("agent_id") || "{}");

  const storeObject = {
    ...existingData,
    [agent_id]: {
      ...existingData[agent_id],
      [activeInstance]: state.history,
    },
  };

  localStorage.setItem("agent_id", JSON.stringify(storeObject));
};

const validateNumber = (number: string) => {
  const regex = new RegExp(/^(?=.{1,30}$)([+]*[0-9a-zA-Z]+[#()+ -]*)/);
  return regex.test(number);
};

/**
 * Initiates a call process by validating the number, checking network connectivity,
 * and ensuring eligibility for voice calls.
 *
 * @param number - The phone number to dial.
 * @param dialInProgress - A flag indicating if a call is already in progress.
 * @param onDialStart - Callback function to execute when dialing starts.
 * @param onDialEnd - Callback function to execute when dialing ends.
 */

export const makeCall = async ({
  number,
  dialCallInProgress,
}: MakeCallOptions) => {
  if (dialCallInProgress) return;

  const cleanedNumber = number
    .replace(/ /g, "")
    .replace(/-/g, "")
    .replace(/\(/g, "")
    .replace(/\)/g, "");

  if (!validateNumber(number)) {
    showErrorToast("Please enter valid number", "invalidNumber");
    return;
  }

  storeRTK.dispatch(isDialCallInProgress(true));
  if (!navigator.onLine) {
    storeRTK.dispatch(isDialCallInProgress(false));
    Emitter.emit(SHOW_NOTIFICATION, {
      message: CALL_CANNOT_DIAL,
      title: "Error",
      type: "warning",
    });
    return;
  }

  try {
    const response = storeRTK.getState().webrtc.isVoiceNetworkAvailable;
    if (response) {
      Emitter.emit(API_CALL_DIAL, {
        phoneNumber: cleanedNumber.startsWith("+")
          ? cleanedNumber
          : "+" + cleanedNumber,
      });
    } else {
      storeRTK.dispatch(isDialCallInProgress(false));
      Emitter.emit(SHOW_NOTIFICATION, {
        message: CALL_CANNOT_DIAL,
        title: "Error",
        type: "warning",
      });
    }
  } catch {
    storeRTK.dispatch(isDialCallInProgress(false));
    Emitter.emit(SHOW_NOTIFICATION, {
      message: CALL_CANNOT_DIAL,
      title: "Error",
      type: "warning",
    });
  }
};

export function b64DecodeUnicode(str: string): string {
  try {
    const binary = atob(str); // decode base64 to binary string
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0)); // convert binary string to byte array
    return new TextDecoder("utf-8").decode(bytes); // decode bytes into a full Unicode string
  } catch (e) {
    console.error("Base64 decode error:", e);
    return "NA";
  }
}

